import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  Loader2,
  BookOpen,
  Link2,
  ChevronRight,
  Filter
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const volumes = [
  { value: 'all', label: 'Todos los volúmenes' },
  { value: 'biblia', label: 'Biblia' },
  { value: 'libro_de_mormon', label: 'Libro de Mormón' },
  { value: 'doctrina_y_convenios', label: 'Doctrina y Convenios' },
  { value: 'perla_de_gran_precio', label: 'Perla de Gran Precio' },
];

const SearchPage = () => {
  const { token } = useAuth();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVolume, setSelectedVolume] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // Cross-references state
  const [themes, setThemes] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themeReferences, setThemeReferences] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  // Search scriptures
  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      toast.error('Ingresa al menos 2 caracteres para buscar');
      return;
    }

    setSearching(true);
    setSearchResults([]);

    try {
      const params = new URLSearchParams({
        q: searchQuery,
        limit: '50'
      });
      if (selectedVolume && selectedVolume !== 'all') {
        params.append('volume', selectedVolume);
      }

      const response = await fetch(`${API_URL}/api/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results);
        setTotalResults(data.total_results);
        if (data.total_results === 0) {
          toast.info('No se encontraron resultados');
        }
      } else {
        toast.error('Error en la búsqueda');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSearching(false);
    }
  }, [searchQuery, selectedVolume, token]);

  // Fetch themes
  const fetchThemes = useCallback(async () => {
    if (themes.length > 0) return;
    
    setLoadingThemes(true);
    try {
      const response = await fetch(`${API_URL}/api/cross-references`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setThemes(data.themes);
      }
    } catch (error) {
      console.error('Error fetching themes:', error);
    } finally {
      setLoadingThemes(false);
    }
  }, [token, themes.length]);

  // Fetch references for a theme
  const fetchThemeReferences = useCallback(async (themeId) => {
    setSelectedTheme(themeId);
    setLoadingRefs(true);
    setThemeReferences([]);

    try {
      const response = await fetch(`${API_URL}/api/cross-references/${themeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setThemeReferences(data.references);
      }
    } catch (error) {
      console.error('Error fetching references:', error);
    } finally {
      setLoadingRefs(false);
    }
  }, [token]);

  const getVolumeForBook = (bookId) => {
    const bomBooks = ['1nefi', '2nefi', 'jacob', 'enos', 'jarom', 'omni', 'mosiah', 'alma', 'helaman', '3nefi', '4nefi', 'mormon', 'eter', 'moroni', 'palabras_de_mormon'];
    const pgpBooks = ['moises', 'abraham', 'jose_smith_historia', 'jose_smith_mateo', 'articulos_de_fe'];
    
    if (bomBooks.includes(bookId)) return 'libro_de_mormon';
    if (bookId === 'dyc') return 'doctrina_y_convenios';
    if (pgpBooks.includes(bookId)) return 'perla_de_gran_precio';
    return 'biblia';
  };

  const volumeLabels = {
    biblia: 'Biblia',
    libro_de_mormon: 'Libro de Mormón',
    doctrina_y_convenios: 'D&C',
    perla_de_gran_precio: 'PGP'
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="search-page">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
            <Search className="h-8 w-8 text-primary" />
            Buscar en las Escrituras
          </h1>
          <p className="text-muted-foreground">
            Busca palabras clave o explora referencias cruzadas por tema
          </p>
        </div>

        <Tabs defaultValue="search" onValueChange={(v) => v === 'references' && fetchThemes()}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="search" data-testid="search-tab">
              <Search className="h-4 w-4 mr-2" />
              Búsqueda
            </TabsTrigger>
            <TabsTrigger value="references" data-testid="references-tab">
              <Link2 className="h-4 w-4 mr-2" />
              Referencias
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Search Form */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Buscar</CardTitle>
                  <CardDescription>
                    Busca palabras o frases en todas las escrituras
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSearch} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Texto a buscar</Label>
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Ej: fe, arrepentimiento, amor..."
                        data-testid="search-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Filtrar por volumen</Label>
                      <Select
                        value={selectedVolume}
                        onValueChange={setSelectedVolume}
                      >
                        <SelectTrigger data-testid="volume-filter">
                          <SelectValue placeholder="Todos los volúmenes" />
                        </SelectTrigger>
                        <SelectContent>
                          {volumes.map((vol) => (
                            <SelectItem key={vol.value} value={vol.value}>
                              {vol.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={searching}
                      data-testid="search-btn"
                    >
                      {searching ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Buscando...
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          Buscar
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Search Results */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">
                    Resultados {totalResults > 0 && `(${totalResults})`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {searchResults.length > 0 ? (
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-4">
                        {searchResults.map((result, idx) => (
                          <Link
                            key={`${result.book}-${result.chapter}-${result.verse}-${idx}`}
                            to={`/study/${result.volume}/${result.book}/${result.chapter}`}
                          >
                            <Card className="hover:shadow-md transition-shadow cursor-pointer">
                              <CardContent className="pt-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="secondary">
                                      {result.book_name} {result.chapter}:{result.verse}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">
                                      {volumeLabels[result.volume]}
                                    </Badge>
                                  </div>
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {result.match_highlight}
                                </p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[500px] text-muted-foreground">
                      <Search className="h-16 w-16 mb-4 opacity-20" />
                      <p className="text-center">
                        {searching ? 'Buscando...' : 'Ingresa un término para buscar en las escrituras'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Cross-References Tab */}
          <TabsContent value="references" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Themes List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Link2 className="h-5 w-5 text-primary" />
                    Temas Doctrinales
                  </CardTitle>
                  <CardDescription>
                    Selecciona un tema para ver sus referencias
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingThemes ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-2">
                        {themes.map((theme) => (
                          <Button
                            key={theme.id}
                            variant={selectedTheme === theme.id ? "secondary" : "ghost"}
                            className="w-full justify-between"
                            onClick={() => fetchThemeReferences(theme.id)}
                            data-testid={`theme-${theme.id}`}
                          >
                            <span>{theme.name}</span>
                            <Badge variant="outline" className="ml-2">
                              {theme.count}
                            </Badge>
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* Theme References */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">
                    {selectedTheme ? (
                      <span className="capitalize">
                        {selectedTheme.replace(/_/g, ' ')}
                      </span>
                    ) : (
                      'Referencias Cruzadas'
                    )}
                  </CardTitle>
                  <CardDescription>
                    {selectedTheme
                      ? 'Pasajes relacionados con este tema'
                      : 'Selecciona un tema para ver las referencias'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingRefs ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : themeReferences.length > 0 ? (
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {themeReferences.map((ref, idx) => (
                          <Link
                            key={`${ref.book}-${ref.chapter}-${idx}`}
                            to={`/study/${getVolumeForBook(ref.book)}/${ref.book}/${ref.chapter}`}
                          >
                            <Card className="hover:shadow-md transition-shadow cursor-pointer">
                              <CardContent className="pt-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <Badge variant="secondary">{ref.ref}</Badge>
                                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                </div>
                                {ref.text && (
                                  <p className="text-sm text-muted-foreground line-clamp-3 scripture-text">
                                    {ref.text}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                      <BookOpen className="h-16 w-16 mb-4 opacity-20" />
                      <p className="text-center">
                        Selecciona un tema doctrinal para ver las referencias relacionadas
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default SearchPage;
